import { useState, useEffect, useCallback } from 'react';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
  functionality: boolean;
  advertising: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  preferences: false,
  functionality: false,
  advertising: false,
};

function ensureGtag() {
  if (typeof window === 'undefined') return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  if (!(window as any).gtag) {
    (window as any).gtag = function gtag(...args: unknown[]) {
      (window as any).dataLayer.push(args);
    };
  }
}

/** Push Google Consent Mode v2 update based on saved preferences */
export function applyConsentMode(prefs: CookiePreferences) {
  if (typeof window === 'undefined') return;
  ensureGtag();
  (window as any).gtag?.('consent', 'update', {
    ad_storage: prefs.advertising ? 'granted' : 'denied',
    ad_user_data: prefs.advertising ? 'granted' : 'denied',
    ad_personalization: prefs.advertising ? 'granted' : 'denied',
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
  });
}

export function useCookiePreferences() {
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load preferences from localStorage
    const savedPreferences = localStorage.getItem('gdpr-cookies-accepted');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        const merged: CookiePreferences = {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          essential: true,
          advertising: parsed.advertising ?? false,
        };
        setPreferences(merged);
        applyConsentMode(merged);
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
        setPreferences(DEFAULT_PREFERENCES);
      }
    }
    setIsLoaded(true);
  }, []);

  const updatePreferences = useCallback((newPreferences: CookiePreferences) => {
    const normalized = { ...newPreferences, essential: true };
    setPreferences(normalized);
    localStorage.setItem('gdpr-cookies-accepted', JSON.stringify(normalized));
    applyConsentMode(normalized);
  }, []);

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      preferences: true,
      functionality: true,
      advertising: true,
    };
    updatePreferences(allAccepted);
  };

  const rejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      preferences: false,
      functionality: false,
      advertising: false,
    };
    updatePreferences(onlyEssential);
  };

  const hasConsent = () => {
    return localStorage.getItem('gdpr-cookies-accepted') !== null;
  };

  const canUseAnalytics = () => {
    return preferences.analytics;
  };

  const canUsePreferences = () => {
    return preferences.preferences;
  };

  const canUseFunctionality = () => {
    return preferences.functionality;
  };

  const canUseAdvertising = () => {
    return preferences.advertising;
  };

  return {
    preferences,
    isLoaded,
    updatePreferences,
    acceptAll,
    rejectAll,
    hasConsent,
    canUseAnalytics,
    canUsePreferences,
    canUseFunctionality,
    canUseAdvertising,
  };
}
